'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useCartStore, useShallow } from 'components/shared/Cart';
import {
  getDeviceType,
  pushToDataLayer,
} from 'components/shared/UserAnalytics';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { analytics, AnalyticsEventProps } from 'lib/analytics/analytics';
import { allowsDataLayer } from 'lib/consent/gate';

import {
  AU_RETAILERS,
  AuRetailer,
  isAuSaleLive,
  isAuSalePreviewOverride,
} from 'components/ring-air/auRetailSaleConfig';
import styles from './AuRetailSaleModal.module.css';

export const AU_RETAIL_SALE_SESSION_KEY = 'au_retail_sale_sep26_seen';

/**
 * Distinguishes AU events from the CA and ES modals' identically named
 * retail_banner_* events in GA4. Forwarding the param needs a GTM mapping.
 */
const AU_SALE_CAMPAIGN = 'au-promo-sep26';

const hasCartItems = (cart: Record<string, any> | null): boolean =>
  (cart?.lines?.edges?.length ?? 0) > 0;

const hasSeenModal = (): boolean => {
  try {
    return window.sessionStorage.getItem(AU_RETAIL_SALE_SESSION_KEY) !== null;
  } catch {
    return true;
  }
};

const markModalSeen = (): boolean => {
  try {
    window.sessionStorage.setItem(AU_RETAIL_SALE_SESSION_KEY, 'shown');
    return true;
  } catch {
    return false;
  }
};

const pushRetailEvent = (event: string, data: AnalyticsEventProps): void => {
  if (allowsDataLayer()) {
    const analyticsWindow = window as Window & {
      dataLayer?: Record<string, unknown>[];
    };
    analyticsWindow.dataLayer ??= [];
    pushToDataLayer(event, data);
  }
  analytics.track(event, data);
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('hidden'));

const RetailerLink = ({
  retailer,
  onClick,
}: {
  retailer: AuRetailer;
  onClick: (retailer: AuRetailer) => void;
}) => {
  const t = useTranslations('ring');
  const retailerName = t(`auRetailSale.retailers.${retailer.id}`);

  return (
    <a
      className={styles.retailerLink}
      href={retailer.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('auRetailSale.retailerAriaLabel', {
        retailer: retailerName,
      })}
      onClick={() => onClick(retailer)}
    >
      {retailer.logoSrc ? (
        // Retailer-approved wordmark artwork.
        <img
          className={styles.retailerLogo}
          src={retailer.logoSrc}
          alt={retailerName}
          loading="eager"
          fetchPriority="high"
        />
      ) : (
        <span className={styles.retailerName}>{retailerName}</span>
      )}
      <span className={styles.buyNow}>
        {t('auRetailSale.buyNow')} <span aria-hidden="true">→</span>
      </span>
    </a>
  );
};

export const AuRetailSaleModal = () => {
  const t = useTranslations('ring');
  const internalPathname = useInternalPathname();
  const { region } = useRegionLocale();
  const { cart, cartHydrationState } = useCartStore(
    useShallow((s) => ({
      cart: s.cart,
      cartHydrationState: s.cartHydrationState,
    })),
  );
  const [isOpen, setIsOpen] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const openedAtRef = useRef(0);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const routePath =
    internalPathname.length > 1
      ? internalPathname.replace(/\/$/, '')
      : internalPathname;

  useEffect(() => {
    // Preview also bypasses the once-per-session guard so QA can re-show it.
    const preview = isAuSalePreviewOverride();
    if (
      region?.toUpperCase() !== 'AU' ||
      routePath !== '/ring/buy' ||
      (!isAuSaleLive() && !preview) ||
      cartHydrationState !== 'ready' ||
      hasCartItems(cart) ||
      (hasSeenModal() && !preview)
    ) {
      return;
    }

    if (!markModalSeen() && !preview) return;
    openedAtRef.current = performance.now();
    setIsOpen(true);
    pushRetailEvent('retail_banner_impression', {
      page: window.location.pathname,
      device: getDeviceType(),
      campaign: AU_SALE_CAMPAIGN,
    });
  }, [cart, cartHydrationState, region, routePath]);

  useEffect(() => {
    if (isOpen && hasCartItems(cart)) setIsOpen(false);
  }, [cart, isOpen]);

  const dismiss = useCallback(() => {
    if (!isOpen) return;
    const secondsVisible = Math.max(
      0,
      Math.round((performance.now() - openedAtRef.current) / 1000),
    );
    pushRetailEvent('retail_banner_dismiss', {
      page: window.location.pathname,
      device: getDeviceType(),
      seconds_visible: secondsVisible,
      campaign: AU_SALE_CAMPAIGN,
    });
    setIsOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    previousActiveElementRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dismiss();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      previousActiveElementRef.current?.focus();
    };
  }, [dismiss, isOpen]);

  const handleRetailerClick = (retailer: AuRetailer) => {
    pushRetailEvent('retail_banner_click', {
      retailer: retailer.id,
      page: window.location.pathname,
      device: getDeviceType(),
      campaign: AU_SALE_CAMPAIGN,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} aria-hidden={false}>
      <div
        ref={dialogRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <div className={styles.header}>
          <h2 className={styles.title} id={titleId}>
            <span className={styles.badge}>{t('auRetailSale.badge')}</span>
            {t('auRetailSale.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.close}
            onClick={dismiss}
            aria-label={t('auRetailSale.closeAriaLabel')}
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <p id={descriptionId} className={styles.description}>
          {t('auRetailSale.description')}
        </p>

        <div className={styles.retailers}>
          {AU_RETAILERS.map((retailer) => (
            <RetailerLink
              key={retailer.id}
              retailer={retailer}
              onClick={handleRetailerClick}
            />
          ))}
        </div>

        <p className={styles.finePrint}>{t('auRetailSale.finePrint')}</p>
      </div>
    </div>
  );
};
